import { DOCUMENT, Injectable, NgZone, computed, effect, inject, signal } from '@angular/core';
import { SALES_ROLE, SalesClaims } from './auth-contracts';
import { decodeJwtPayload, expiryOf, lifetimeOf } from './jwt';
import { SalesRole, roleOf } from './roles';

/** App-specific so no other app on the same origin reads or overwrites it. */
export const SESSION_STORAGE_KEY = 'me-operations-ui.session';

/** Why the session ended; `signed-out` is the only reason that does not come back to this page. */
export type SessionEndReason = 'expired' | 'signed-out' | 'ended-elsewhere';

export interface SignedInRep {
  name: string | null;
  email: string | null;
}

/** The token and the moment, in epoch milliseconds on this browser's clock, the session ends. */
interface HeldSession {
  accessToken: string;
  deadline: number;
}

/**
 * The token lives in localStorage so links opened in a new tab stay signed in. It ends at a deadline measured on
 * this browser's own clock from when the token arrived, plus the lifetime the server set (`exp - iat`), rather than
 * from `exp` against `Date.now()`, so a PC clock that runs ahead or behind does not refuse or stretch the session
 * (`restore()` falls back to `exp` only for an entry stored without a deadline). A timer ends the session at the
 * deadline, so an idle tab goes to Login without a click. Other tabs
 * follow through the `storage` event. Storage access is guarded: it throws in some private modes.
 */
@Injectable({ providedIn: 'root' })
export class SessionStore {
  private readonly document = inject(DOCUMENT);

  private readonly window = this.document.defaultView;

  private readonly zone = inject(NgZone);

  private readonly held = signal<HeldSession | null>(this.restore(), {
    equal: (a, b) => a?.accessToken === b?.accessToken && a?.deadline === b?.deadline,
  });

  private readonly token = computed(() => this.held()?.accessToken ?? null);

  private readonly lastEndReason = signal<SessionEndReason | null>(null);

  readonly endReason = this.lastEndReason.asReadonly();

  readonly claims = computed<SalesClaims | null>(() => {
    const token = this.token();

    return token === null ? null : decodeJwtPayload(token);
  });

  readonly role = computed<SalesRole | null>(() => {
    const claims = this.claims();

    return claims === null ? null : roleOf(claims);
  });

  readonly rep = computed<SignedInRep | null>(() => {
    const claims = this.claims();

    if (claims === null) {
      return null;
    }

    return {
      name: typeof claims.name === 'string' && claims.name.trim() !== '' ? claims.name : null,
      email: typeof claims.email === 'string' ? claims.email : null,
    };
  });

  /** True while a token is held, even an expired one. */
  readonly hasToken = computed(() => this.token() !== null);

  constructor() {
    // Every change of token or deadline (a renewal here, one from another tab, sign-out) re-runs this effect, so
    // the cleanup disarms the old timer and a new one counts down to the new deadline. An already-expired
    // session gets no timer: it is held on purpose so Login can say it expired.
    effect((onCleanup) => {
      const held = this.held();
      const remaining = held === null ? 0 : held.deadline - Date.now();

      if (held === null || remaining <= 0) {
        return;
      }

      const armedFor = held.accessToken;

      // Outside the zone: a pending hour-long timer inside it would keep the app from ever being stable while signed
      // in. Back inside to end the session, so the shell's redirect runs with change detection.
      const timer = this.zone.runOutsideAngular(() =>
        setTimeout(() => {
          if (this.token() === armedFor) {
            this.zone.run(() => this.clear('expired'));
          }
        }, remaining),
      );

      onCleanup(() => clearTimeout(timer));
    });

    // The timer can run late after the computer sleeps or the browser freezes a background tab, so the deadline is
    // checked again whenever the tab comes back into view or takes focus.
    const endIfPastDeadline = (): void => {
      if (this.hasToken() && !this.isSignedIn()) {
        this.zone.run(() => this.clear('expired'));
      }
    };
    this.document.addEventListener('visibilitychange', () => {
      if (this.document.visibilityState === 'visible') {
        endIfPastDeadline();
      }
    });
    this.window?.addEventListener('focus', endIfPastDeadline);

    this.window?.addEventListener('storage', (event) => {
      // key === null is localStorage.clear() in another tab.
      if (event.key === SESSION_STORAGE_KEY || event.key === null) {
        const restored = this.restore();
        const held = this.token();

        // Already signed out here (this tab's own timer got there first): keep that reason.
        if (restored === null && held === null) {
          return;
        }

        // A token that had already expired when it vanished means the session timed out, not that
        // the rep signed out in another tab, so every tab reports the same reason.
        this.lastEndReason.set(
          restored === null && held !== null ? (this.isSignedIn() ? 'ended-elsewhere' : 'expired') : null,
        );
        this.held.set(restored);
      }
    });
  }

  accessToken(): string | null {
    return this.token();
  }

  /** Time-dependent, so a method, not a signal. */
  isSignedIn(now: number = Date.now()): boolean {
    const held = this.held();

    return held !== null && held.deadline > now;
  }

  /**
   * Refuses a token that is unreadable, has no usable lifetime (`exp` after `iat`) or is not a Sales token (it would
   * sign the rep out on the next reload). A refusal changes nothing, so it never signs other tabs out.
   */
  start(accessToken: string | null | undefined): boolean {
    if (typeof accessToken !== 'string') {
      return false;
    }

    const deadline = deadlineOnArrival(accessToken, Date.now());

    if (deadline === null) {
      return false;
    }

    this.lastEndReason.set(null);
    this.save({ accessToken, deadline });

    return true;
  }

  /**
   * Swaps in a renewed token for the same rep and session: the one a profile save returns (it carries the saved
   * email and name, EP-40) or the `renewed-token` the interceptor reads off an API response (a fresh lifetime, the same
   * claims). The session now ends one lifetime after this call, on this browser's clock. Returns whether the token
   * was taken.
   *
   * Pass `forToken` = `accessToken()` captured before the save request: if the session changed while the request
   * ran (signed out, another sign-in or renewal, here or in another tab), nothing changes. A token that is unusable
   * (no lifetime), not a Sales token or for another rep is refused the same way. With storage unreadable (blocked site data) the
   * stored token cannot be compared, so it is refused too and the current token stays.
   */
  renew(accessToken: string, forToken: string | null): boolean {
    const current = this.token();

    if (current === null || current !== forToken || this.restore()?.accessToken !== forToken) {
      return false;
    }

    const deadline = deadlineOnArrival(accessToken, Date.now());

    if (deadline === null || decodeJwtPayload(accessToken)?.id !== this.claims()?.id) {
      return false;
    }

    this.save({ accessToken, deadline });

    return true;
  }

  /** Forgets the token here and, through the storage event, in other tabs. The shell then redirects to Login. */
  clear(reason: SessionEndReason): void {
    this.lastEndReason.set(reason);
    this.held.set(null);

    try {
      this.window?.localStorage.removeItem(SESSION_STORAGE_KEY);
    } catch {
      // Nothing more to do: the in-memory token is already gone.
    }
  }

  private save(session: HeldSession): void {
    this.held.set(session);

    try {
      this.window?.localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(session));
    } catch {
      // Storage unavailable: the session still works in this tab until reload.
    }
  }

  /**
   * An expired Sales token is kept (held, not signed in) so Login can say it expired rather than never existed.
   * An entry written before the deadline was stored has none, so its `exp` stands in, the old rule: a rep signed in
   * before that release stays signed in.
   */
  private restore(): HeldSession | null {
    let raw: string | null;

    try {
      raw = this.window?.localStorage.getItem(SESSION_STORAGE_KEY) ?? null;
    } catch {
      return null;
    }

    if (raw === null) {
      return null;
    }

    try {
      const entry = JSON.parse(raw) as { accessToken?: unknown; deadline?: unknown } | null;
      const accessToken = entry?.accessToken;
      const storedDeadline = entry?.deadline;

      if (typeof accessToken !== 'string' || !isSalesToken(accessToken)) {
        return null;
      }

      const claims = decodeJwtPayload(accessToken);
      const deadline =
        typeof storedDeadline === 'number' && Number.isFinite(storedDeadline)
          ? storedDeadline
          : claims === null
            ? null
            : expiryOf(claims);

      return deadline === null ? null : { accessToken, deadline };
    } catch {
      return null;
    }
  }
}

/** A Sales token, expired or not. */
function isSalesToken(token: string): boolean {
  const claims = decodeJwtPayload(token);

  return claims !== null && expiryOf(claims) !== null && hasSalesRole(claims.roles);
}

/**
 * When the session for a token that arrived at `arrivedAt` ends, or null when it is not a usable Sales token. The
 * server sets the lifetime (`exp - iat`); the browser's clock sets the start.
 */
function deadlineOnArrival(token: string, arrivedAt: number): number | null {
  const claims = decodeJwtPayload(token);
  const lifetime = claims === null ? null : lifetimeOf(claims);

  return lifetime !== null && isSalesToken(token) ? arrivedAt + lifetime : null;
}

function hasSalesRole(roles: unknown): boolean {
  if (typeof roles === 'string') {
    return roles === SALES_ROLE;
  }

  return Array.isArray(roles) && roles.includes(SALES_ROLE);
}
